import React, { useState } from 'react'
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  Container,
  Menu,
  MenuItem,
  Avatar,
  Chip,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material'
import {
  ShoppingCart as ShopIcon,
  History as HistoryIcon,
  ContactMail as AddressBookIcon,
  Gavel as LegalIcon,
  AdminPanelSettings as AdminIcon,
  Menu as MenuIcon,
  LockOutlined as LockIcon,
} from '@mui/icons-material'
import { useNavigate, useLocation } from 'react-router-dom'
import { useCustomerAuth } from '@/context/CustomerAuthContext'

interface CustomerLayoutProps {
  children: React.ReactNode
}

export const CustomerLayout: React.FC<CustomerLayoutProps> = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, isLoggedIn, logout } = useCustomerAuth()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [mobileOpen, setMobileOpen] = useState(false)

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseUserMenu = () => {
    setAnchorEl(null)
  }

  const handleLogout = () => {
    handleCloseUserMenu()
    logout()
    navigate('/login')
  }

  const navItems = [
    { label: '商品注文', path: '/checkout', icon: <ShopIcon fontSize="small" /> },
    { label: '注文履歴', path: '/mypage/orders', icon: <HistoryIcon fontSize="small" /> },
    { label: 'マイ配送先', path: '/mypage/addresses', icon: <AddressBookIcon fontSize="small" /> },
    { label: '特商法表記', path: '/tokushohon', icon: <LegalIcon fontSize="small" /> },
  ]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* 顧客ポータル用 ヘッダーバー */}
      <AppBar position="sticky" elevation={1} sx={{ backgroundColor: '#ffffff', color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
            {/* モバイル用メニューアイコン */}
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setMobileOpen(true)}
              sx={{ display: { md: 'none' }, mr: 1 }}
            >
              <MenuIcon />
            </IconButton>

            {/* ブランドロゴ */}
            <Box
              onClick={() => navigate('/checkout')}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 1 }}
            >
              <Typography
                variant="h6"
                noWrap
                sx={{
                  fontWeight: 700,
                  letterSpacing: '.05rem',
                  color: '#0f172a',
                  background: 'linear-gradient(45deg, #2563eb, #1d4ed8)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                HighLandirect <Typography component="span" variant="caption" sx={{ color: '#2563eb', fontWeight: 600, ml: 0.5, px: 1, py: 0.2, bgcolor: '#eff6ff', borderRadius: 1 }}>オンライン注文</Typography>
              </Typography>
            </Box>

            {/* デスクトップ用 ナビゲーション */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center' }}>
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  startIcon={item.icon}
                  sx={{
                    color: location.pathname === item.path ? '#2563eb' : '#475569',
                    fontWeight: location.pathname === item.path ? 700 : 500,
                    backgroundColor: location.pathname === item.path ? '#eff6ff' : 'transparent',
                    '&:hover': { backgroundColor: '#f1f5f9' },
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 2,
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>

            {/* ユーザーアカウント & 管理画面切り替え */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {/* 管理画面への切り替えボタン */}
              <Button
                variant="outlined"
                size="small"
                startIcon={<AdminIcon />}
                onClick={() => navigate('/orders')}
                sx={{
                  borderColor: '#cbd5e1',
                  color: '#475569',
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  display: { xs: 'none', sm: 'inline-flex' },
                }}
              >
                管理者ポータルへ
              </Button>

              {isLoggedIn && user ? (
                <>
                  <Chip
                    avatar={<Avatar alt={user.Name} src="https://lh3.googleusercontent.com/a/default-user" />}
                    label={user.Name}
                    onClick={handleOpenUserMenu}
                    variant="outlined"
                    sx={{ cursor: 'pointer', fontWeight: 600, borderColor: '#cbd5e1' }}
                  />
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleCloseUserMenu}
                    PaperProps={{
                      elevation: 3,
                      sx: { mt: 1, minWidth: 200, borderRadius: 2 },
                    }}
                  >
                    <Box sx={{ px: 2, py: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {user.Name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {user.Email}
                      </Typography>
                      <Chip label="Google連携済み" size="small" color="success" sx={{ mt: 0.5, height: 20, fontSize: '0.65rem' }} />
                    </Box>
                    <Divider />
                    <MenuItem onClick={() => { handleCloseUserMenu(); navigate('/mypage/orders'); }}>
                      マイ注文履歴
                    </MenuItem>
                    <MenuItem onClick={() => { handleCloseUserMenu(); navigate('/mypage/addresses'); }}>
                      マイ配送先一覧
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleLogout} sx={{ color: '#ef4444' }}>
                      ログアウト
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<LockIcon />}
                  onClick={() => navigate('/login')}
                  sx={{
                    bgcolor: '#2563eb',
                    '&:hover': { bgcolor: '#1d4ed8' },
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Googleログイン
                </Button>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* モバイル用 ドロワーメニュー */}
      <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <Box sx={{ width: 250, p: 2 }}>
          <Typography variant="h6" fontWeight={700} color="primary" sx={{ mb: 2 }}>
            HighLandirect
          </Typography>
          <List>
            {navItems.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  onClick={() => {
                    navigate(item.path)
                    setMobileOpen(false)
                  }}
                  selected={location.pathname === item.path}
                >
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
            <Divider sx={{ my: 1 }} />
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  navigate('/terms')
                  setMobileOpen(false)
                }}
              >
                <ListItemText primary="利用規約 & プライバシーポリシー" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  navigate('/orders')
                  setMobileOpen(false)
                }}
              >
                <ListItemText primary="管理者画面へ切り替え" />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* メインコンテンツ */}
      <Container component="main" maxWidth="lg" sx={{ flexGrow: 1, py: 4 }}>
        {children}
      </Container>

      {/* 顧客ポータル用 フッター（特商法・規約・法令表記リンク） */}
      <Box component="footer" sx={{ py: 4, bgcolor: '#0f172a', color: '#94a3b8', mt: 'auto' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="#f8fafc">
                HighLandirect オンラインストア
              </Typography>
              <Typography variant="caption" color="#64748b">
                安全なStripeカード決済 (オーソリ・与信枠保持対応) / 日本郵便住所検索連携
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button size="small" onClick={() => navigate('/tokushohon')} sx={{ color: '#cbd5e1', '&:hover': { color: '#ffffff' }, textTransform: 'none' }}>
                特定商取引法に基づく表記
              </Button>
              <Button size="small" onClick={() => navigate('/terms')} sx={{ color: '#cbd5e1', '&:hover': { color: '#ffffff' }, textTransform: 'none' }}>
                利用規約
              </Button>
              <Button size="small" onClick={() => navigate('/terms')} sx={{ color: '#cbd5e1', '&:hover': { color: '#ffffff' }, textTransform: 'none' }}>
                プライバシーポリシー
              </Button>
            </Box>
          </Box>
          <Divider sx={{ my: 2, borderColor: '#1e293b' }} />
          <Typography variant="caption" textAlign="center" display="block" color="#64748b">
            © 2026 HighLandirect. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  )
}
export default CustomerLayout
